import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { usePageTitle } from '../context/PageChromeContext';
import { FiPlus, FiMapPin, FiX, FiFilter } from 'react-icons/fi';
import { formatJobSalary } from '../utils/jobSalary';
import { useListViewPreferences } from '../hooks/useListViewPreferences';
import ListColumnPicker from '../components/ListColumnPicker';
import ListSearchBar from '../components/ListSearchBar';
import ListPager from '../components/ListPager';
import {
  LIST_VIEW_KEYS,
  JOB_COLUMNS_STAFF,
  JOB_COLUMNS_CANDIDATE,
} from '../config/listViewColumns';
import { queryKeys, staleTimes } from '../config/queryKeys';
import { DEFAULT_LIST_LIMIT, normalizePagedList } from '../utils/listPagination';
import { useAtsMatchRun } from '../context/AtsMatchRunContext';
import { useBulkSelection } from '../hooks/useBulkSelection';
import JobFormModal from '../components/JobFormModal';
import BulkActionBar from '../components/BulkActionBar';
import IconButton from '../components/IconButton';
import { downloadCsv } from '../utils/exportCsv';
import { summarizeAiMatchResults } from '../utils/aiMatchMessages';
import { LISTING_TYPE_OPTIONS, listingTypeBadgeClass } from '../utils/listingType';
import QueryLoadStatus, { QueryLoadingPanel } from '../components/QueryLoadStatus';
import './Jobs.css';
import { appAlert, appConfirm } from '../utils/appDialog';

const JOB_EXPORT_COLUMNS = [
  { key: 'company', label: 'Company' },
  { key: 'title', label: 'Job Title' },
  { key: 'location', label: 'Location' },
  { key: 'employment_type', label: 'Employment Type' },
  {
    key: 'salary',
    label: 'Salary',
    format: (row) => formatJobSalary(row) || '',
  },
  { key: 'status', label: 'Status' },
  { key: 'listing_type', label: 'Listing' },
];

const Jobs = () => {
  const { user } = useAuth();
  usePageTitle(user?.role === 'candidate' ? 'Jobs' : 'Job Openings');
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { startJobMatch } = useAtsMatchRun();
  const [search, setSearch] = useState('');
  const [location, setLocation] = useState('');
  const [employmentType, setEmploymentType] = useState('');
  const [listingType, setListingType] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showPostJobModal, setShowPostJobModal] = useState(false);
  const [jobViewMode, setJobViewMode] = useState('matched');
  const [page, setPage] = useState(1);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);

  useEffect(() => {
    setPage(1);
  }, [search, location, employmentType, listingType, jobViewMode, user?.role]);

  const listFilters = {
    search,
    location,
    employmentType,
    listingType,
    jobViewMode,
    role: user?.role,
    page,
    limit: DEFAULT_LIST_LIMIT,
  };

  const { data: jobsPage, isLoading, isFetching } = useQuery(
    queryKeys.jobs(listFilters),
    () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (location) params.append('location', location);
      if (employmentType) params.append('employment_type', employmentType);
      if (listingType) params.append('listing_type', listingType);
      params.set('limit', String(DEFAULT_LIST_LIMIT));
      params.set('page', String(page));
      const isCandidateBrowse = user?.role === 'candidate' && jobViewMode === 'browse';
      const endpoint = isCandidateBrowse
        ? `/jobs/opportunities?${params.toString()}`
        : `/jobs?${params.toString()}`;
      return api.get(endpoint).then((res) => normalizePagedList(res.data, { itemsKey: 'jobs' }));
    },
    { enabled: Boolean(user?.id), keepPreviousData: true, staleTime: staleTimes.lists }
  );

  const listingTypeMutation = useMutation(
    ({ id, listing_type }) => api.patch(`/jobs/${id}/listing-type`, { listing_type }),
    {
      onSuccess: () => queryClient.invalidateQueries(queryKeys.jobsRoot),
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const isCandidate = user?.role === 'candidate';
  const isStaff = hasPermission('job_view') && !isCandidate;
  const canCreateJob = hasPermission('job_create');

  const jobColumnDefs = isCandidate ? JOB_COLUMNS_CANDIDATE : JOB_COLUMNS_STAFF;
  const listViewKey = isCandidate ? LIST_VIEW_KEYS.JOBS_CANDIDATE : LIST_VIEW_KEYS.JOBS_STAFF;

  const {
    colIndex,
    toggleColumn,
    moveColumn,
    resetColumns,
    visibleColumnIds,
    allColumns,
    getColumnProps,
    ResizeHandle,
    tableRef,
  } = useListViewPreferences(listViewKey, jobColumnDefs);

  const jobList = jobsPage?.items || [];
  const pagination = jobsPage?.pagination;
  const {
    selectedItems,
    selectedCount,
    allSelected,
    someSelected,
    toggleOne,
    toggleAll,
    clearSelection,
    isSelected,
  } = useBulkSelection(jobList);

  const handleJobClick = (job) => {
    navigate(`/jobs/${job.id}`);
  };

  const handleBulkExport = () => {
    downloadCsv(selectedItems, JOB_EXPORT_COLUMNS, `jobs-export-${Date.now()}.csv`);
  };

  const handleBulkDelete = async () => {
    if (!await appConfirm(`Soft-delete ${selectedCount} job(s)? They will be marked as deleted.`)) return;
    setBulkBusy(true);
    setBulkAction('delete');
    try {
      await Promise.all(selectedItems.map((job) => api.delete(`/jobs/${job.id}`)));
      queryClient.invalidateQueries(queryKeys.jobsRoot);
      clearSelection();
    } catch (e) {
      void appAlert(e.response?.data?.error || e.message);
    } finally {
      setBulkBusy(false);
      setBulkAction(null);
    }
  };

  const handleBulkMatch = async () => {
    if (!await appConfirm(`Run AI matching for ${selectedCount} job(s)?`)) return;
    setBulkBusy(true);
    setBulkAction('match');
    try {
      const results = await Promise.all(
        selectedItems.map((job) =>
          startJobMatch(job.id, { label: job.title, notify: false }).then((res) => res.data)
        )
      );
      void appAlert(summarizeAiMatchResults(results));
      clearSelection();
    } catch (e) {
      void appAlert(e.response?.data?.error || e.message);
    } finally {
      setBulkBusy(false);
      setBulkAction(null);
    }
  };

  if (isLoading && jobsPage == null) {
    return (
      <QueryLoadingPanel
        isLoading={isLoading}
        isFetching={isFetching}
        hasData={false}
        initialLabel="Loading jobs…"
      />
    );
  }

  const hasJobFilters = Boolean(search || location || employmentType || listingType);

  return (
    <div className="jobs-page list-page" aria-busy={isFetching && jobsPage != null}>
      <div className={`page-header${selectedCount > 0 ? ' page-header--bulk' : ''}`}>
        {selectedCount > 0 ? (
          <BulkActionBar
            className="bulk-action-bar--header"
            count={selectedCount}
            onClear={clearSelection}
            onExport={handleBulkExport}
            onDelete={isStaff ? handleBulkDelete : undefined}
            onMatch={isStaff ? handleBulkMatch : undefined}
            showDelete={isStaff}
            showMatch={isStaff}
            busy={bulkBusy}
            busyAction={bulkAction}
          />
        ) : (
          <h1>Job Openings</h1>
        )}
        <div className="list-page-header-actions page-toolbar">
          <QueryLoadStatus
            isLoading={isLoading}
            isFetching={isFetching}
            hasData={jobsPage != null}
          />
          {isCandidate && (
            <div className="job-mode-toggle">
              <button
                type="button"
                className={jobViewMode === 'matched' ? 'active' : ''}
                onClick={() => setJobViewMode('matched')}
              >
                My matches
              </button>
              <button
                type="button"
                className={jobViewMode === 'browse' ? 'active' : ''}
                onClick={() => setJobViewMode('browse')}
              >
                Browse all
              </button>
            </div>
          )}
          <ListColumnPicker
            allColumns={allColumns}
            visibleColumnIds={visibleColumnIds}
            onToggle={toggleColumn}
            onMove={moveColumn}
            onReset={resetColumns}
          />
          {canCreateJob && (
            <IconButton
              icon={FiPlus}
              label="Post new job"
              variant="primary"
              onClick={() => setShowPostJobModal(true)}
            />
          )}
        </div>
      </div>

      <div className="list-filters-panel list-toolbar-panel">
        <div className="filter-row list-toolbar-row">
          <div className="filter-group filter-group--wide list-toolbar-search">
            <ListSearchBar
              id="jobs-filter-search"
              value={search}
              onChange={setSearch}
              placeholder="Search title, company, skills…"
            />
          </div>
          <IconButton
            icon={FiFilter}
            label={showFilters ? 'Hide filters' : 'More filters'}
            active={showFilters || Boolean(location || employmentType || listingType)}
            onClick={() => setShowFilters(!showFilters)}
          />
          {hasJobFilters && (
            <IconButton
              icon={FiX}
              label="Clear filters"
              onClick={() => {
                setSearch('');
                setLocation('');
                setEmploymentType('');
                setListingType('');
              }}
            />
          )}
        </div>
        {showFilters && (
          <div className="filter-row list-toolbar-advanced">
            <div className="filter-group">
              <label htmlFor="jobs-filter-location">Location</label>
              <input
                id="jobs-filter-location"
                type="text"
                placeholder="City or region"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label htmlFor="jobs-filter-type">Employment</label>
              <select
                id="jobs-filter-type"
                value={employmentType}
                onChange={(e) => setEmploymentType(e.target.value)}
              >
                <option value="">All types</option>
                <option value="full-time">Full time</option>
                <option value="part-time">Part time</option>
                <option value="contract">Contract</option>
                <option value="remote">Remote</option>
              </select>
            </div>
            {isStaff && (
              <div className="filter-group">
                <label htmlFor="jobs-filter-listing">Listing</label>
                <select
                  id="jobs-filter-listing"
                  value={listingType}
                  onChange={(e) => setListingType(e.target.value)}
                >
                  <option value="">All listings</option>
                  {LISTING_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="jobs-table-container">
        <table ref={tableRef} className="table jobs-table" style={{ tableLayout: 'fixed', width: '100%' }}>
          <thead>
            <tr>
              {visibleColumnIds.map((columnId) => {
                const idx = colIndex(columnId);
                if (columnId === 'select') {
                  return (
                    <th key={columnId} {...getColumnProps(idx)} className="list-select-cell">
                      <input
                        type="checkbox"
                        aria-label="Select all jobs"
                        checked={allSelected && jobList.length > 0}
                        ref={(el) => {
                          if (el) el.indeterminate = someSelected && !allSelected;
                        }}
                        onChange={toggleAll}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                  );
                }
                const labelMap = {
                  company: 'Company',
                  title: 'Job Title',
                  location: 'Location',
                  employment_type: 'Type',
                  salary: 'Salary',
                  match_score: 'Match',
                  match_coverage: 'Match coverage',
                  listing: 'Listing',
                  status: 'Status',
                };
                if (columnId === 'match_score' && !isCandidate) return null;
                if ((columnId === 'match_coverage' || columnId === 'listing') && !isStaff) return null;
                return (
                  <th
                    key={columnId}
                    {...getColumnProps(idx)}
                    className={columnId === 'status' ? 'col-status' : undefined}
                    title={
                      columnId === 'match_coverage'
                        ? 'Distinct candidates with a stored match / all active candidates'
                        : columnId === 'listing'
                          ? 'Where this job is published'
                          : undefined
                    }
                  >
                    {labelMap[columnId] || columnId}
                    <ResizeHandle index={idx} />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {jobList.length > 0 ? (
              jobList.map((job) => (
                <tr
                  key={job.id}
                  className={`job-row${isSelected(job.id) ? ' row-selected' : ''}`}
                  onClick={() => handleJobClick(job)}
                >
                  {visibleColumnIds.map((columnId) => {
                    if (columnId === 'select') {
                      return (
                        <td key={columnId} className="list-select-cell" onClick={(e) => e.stopPropagation()}>
                          {someSelected ? (
                            <input
                              type="checkbox"
                              aria-label={`Select ${job.title}`}
                              checked={isSelected(job.id)}
                              onChange={() => toggleOne(job.id)}
                            />
                          ) : null}
                        </td>
                      );
                    }
                    if (columnId === 'company') {
                      return (
                        <td key={columnId}>
                          <strong>{job.company}</strong>
                        </td>
                      );
                    }
                    if (columnId === 'title') {
                      return (
                        <td key={columnId}>
                          <div className="job-title-cell">
                            <strong>{job.title}</strong>
                            {job.required_skills && job.required_skills.length > 0 && (
                              <div className="job-skills-inline">
                                {job.required_skills.slice(0, 2).map((skill, skillIdx) => (
                                  <span key={skillIdx} className="skill-tag-small">{skill}</span>
                                ))}
                                {job.required_skills.length > 2 && (
                                  <span className="skill-tag-small">+{job.required_skills.length - 2}</span>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      );
                    }
                    if (columnId === 'location') {
                      return (
                        <td key={columnId}>
                          {job.location ? (
                            <span className="job-location-inline">
                              <FiMapPin /> {job.location}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                      );
                    }
                    if (columnId === 'employment_type') {
                      return (
                        <td key={columnId}>
                          {job.employment_type ? (
                            <span className="job-type-badge">{job.employment_type}</span>
                          ) : (
                            '—'
                          )}
                        </td>
                      );
                    }
                    if (columnId === 'salary') {
                      return (
                        <td key={columnId}>
                          {(job.salary_min || job.salary_max) ? (
                            <span className="job-salary-inline">{formatJobSalary(job)}</span>
                          ) : (
                            <span className="text-muted">Not disclosed</span>
                          )}
                        </td>
                      );
                    }
                    if (columnId === 'match_score') {
                      if (!isCandidate) return null;
                      return (
                        <td key={columnId}>
                          <strong className="tabular-nums">
                            {job.match_score != null ? `${Math.round(Number(job.match_score))}%` : '—'}
                          </strong>
                        </td>
                      );
                    }
                    if (columnId === 'match_coverage') {
                      if (!isStaff) return null;
                      return (
                        <td key={columnId}>
                          <span
                            className="job-match-coverage"
                            title={`${job.matched_candidate_count || 0} candidates with a match`}
                          >
                            <span className="tabular-nums">
                              {job.match_percentage != null ? `${job.match_percentage}%` : '0%'}
                            </span>
                            <span className="job-match-coverage__count">
                              ({job.matched_candidate_count ?? 0})
                            </span>
                          </span>
                        </td>
                      );
                    }
                    if (columnId === 'listing') {
                      if (!isStaff) return null;
                      return (
                        <td key={columnId} onClick={(e) => e.stopPropagation()}>
                          <select
                            className="listing-type-select"
                            value={job.listing_type || 'internal'}
                            onChange={(e) =>
                              listingTypeMutation.mutate({ id: job.id, listing_type: e.target.value })
                            }
                            disabled={listingTypeMutation.isLoading && listingTypeMutation.variables?.id === job.id}
                            aria-label={`Listing type for ${job.title}`}
                          >
                            {LISTING_TYPE_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </td>
                      );
                    }
                    if (columnId === 'status') {
                      return (
                        <td key={columnId} className="col-status">
                          <span
                            className={`badge badge-${job.status === 'active' ? 'success' : job.status === 'closed' ? 'warning' : job.status === 'draft' ? 'info' : 'danger'}`}
                          >
                            {job.status}
                          </span>
                          {isStaff && job.listing_type === 'web' && (
                            <span className={`badge badge-${listingTypeBadgeClass('web')} badge-inline`}>
                              Career site
                            </span>
                          )}
                        </td>
                      );
                    }
                    return null;
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={Math.max(visibleColumnIds.length, 1)} className="empty-state">
                  {isCandidate ? 'No matched jobs yet. An admin can run AI matching in Settings.' : 'No jobs found'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ListPager
        page={pagination?.page || page}
        pageCount={pagination?.pageCount || 1}
        total={pagination?.total}
        limit={pagination?.limit || DEFAULT_LIST_LIMIT}
        disabled={isFetching}
        onPageChange={(next) => {
          clearSelection();
          setPage(next);
        }}
      />

      <JobFormModal
        open={showPostJobModal}
        onClose={() => setShowPostJobModal(false)}
      />
    </div>
  );
};

export default Jobs;

